/* eslint-disable @typescript-eslint/naming-convention */
var _PayloadReader_instances, _PayloadReader_view, _PayloadReader_end, _PayloadReader_require;
import { __classPrivateFieldGet, __classPrivateFieldSet } from "tslib";
export const COMMAND_MAGIC = 0x42445355;
export const PROTOCOL_VERSION = 5;
export const MISSING_OFFSET = 0xffffffff;
export var Command;
(function (Command) {
    Command[Command["Scene"] = 1] = "Scene";
    Command[Command["Texture"] = 2] = "Texture";
    Command[Command["Material"] = 3] = "Material";
    Command[Command["TransformNode"] = 4] = "TransformNode";
    Command[Command["Skeleton"] = 5] = "Skeleton";
    Command[Command["Geometry"] = 6] = "Geometry";
    Command[Command["Mesh"] = 7] = "Mesh";
    Command[Command["Instance"] = 8] = "Instance";
    Command[Command["Animation"] = 9] = "Animation";
    Command[Command["AnalyticPrimitive"] = 10] = "AnalyticPrimitive";
    Command[Command["ThinInstances"] = 11] = "ThinInstances";
    Command[Command["MorphTarget"] = 12] = "MorphTarget";
})(Command || (Command = {}));
export var AnalyticPrimitiveType;
(function (AnalyticPrimitiveType) {
    AnalyticPrimitiveType[AnalyticPrimitiveType["Cube"] = 0] = "Cube";
    AnalyticPrimitiveType[AnalyticPrimitiveType["Sphere"] = 1] = "Sphere";
    AnalyticPrimitiveType[AnalyticPrimitiveType["Cylinder"] = 2] = "Cylinder";
    AnalyticPrimitiveType[AnalyticPrimitiveType["Cone"] = 3] = "Cone";
})(AnalyticPrimitiveType || (AnalyticPrimitiveType = {}));
export var PrimitiveAxis;
(function (PrimitiveAxis) {
    PrimitiveAxis[PrimitiveAxis["X"] = 0] = "X";
    PrimitiveAxis[PrimitiveAxis["Y"] = 1] = "Y";
    PrimitiveAxis[PrimitiveAxis["Z"] = 2] = "Z";
})(PrimitiveAxis || (PrimitiveAxis = {}));
export var AnimationTarget;
(function (AnimationTarget) {
    AnimationTarget[AnimationTarget["Node"] = 0] = "Node";
    AnimationTarget[AnimationTarget["Bone"] = 1] = "Bone";
    AnimationTarget[AnimationTarget["MorphTarget"] = 2] = "MorphTarget";
})(AnimationTarget || (AnimationTarget = {}));
export var AnimationProperty;
(function (AnimationProperty) {
    AnimationProperty[AnimationProperty["Position"] = 0] = "Position";
    AnimationProperty[AnimationProperty["RotationQuaternion"] = 1] = "RotationQuaternion";
    AnimationProperty[AnimationProperty["Scaling"] = 2] = "Scaling";
    AnimationProperty[AnimationProperty["Matrix"] = 3] = "Matrix";
    AnimationProperty[AnimationProperty["Influence"] = 4] = "Influence";
})(AnimationProperty || (AnimationProperty = {}));
export var MaterialFlags;
(function (MaterialFlags) {
    MaterialFlags[MaterialFlags["DoubleSided"] = 1] = "DoubleSided";
    MaterialFlags[MaterialFlags["Unlit"] = 2] = "Unlit";
    MaterialFlags[MaterialFlags["AlphaBlend"] = 4] = "AlphaBlend";
})(MaterialFlags || (MaterialFlags = {}));
export var TextureOutputChannel;
(function (TextureOutputChannel) {
    TextureOutputChannel[TextureOutputChannel["R"] = 0] = "R";
    TextureOutputChannel[TextureOutputChannel["G"] = 1] = "G";
    TextureOutputChannel[TextureOutputChannel["B"] = 2] = "B";
    TextureOutputChannel[TextureOutputChannel["A"] = 3] = "A";
    TextureOutputChannel[TextureOutputChannel["RGB"] = 4] = "RGB";
})(TextureOutputChannel || (TextureOutputChannel = {}));
export var USDTextureColorSpace;
(function (USDTextureColorSpace) {
    USDTextureColorSpace[USDTextureColorSpace["Auto"] = 0] = "Auto";
    USDTextureColorSpace[USDTextureColorSpace["Raw"] = 1] = "Raw";
    USDTextureColorSpace[USDTextureColorSpace["SRGB"] = 2] = "SRGB";
})(USDTextureColorSpace || (USDTextureColorSpace = {}));
export var MeshFlags;
(function (MeshFlags) {
    MeshFlags[MeshFlags["DoubleSided"] = 1] = "DoubleSided";
    MeshFlags[MeshFlags["LeftHanded"] = 2] = "LeftHanded";
})(MeshFlags || (MeshFlags = {}));
export var GeometryFlags;
(function (GeometryFlags) {
    GeometryFlags[GeometryFlags["Normals"] = 1] = "Normals";
    GeometryFlags[GeometryFlags["Tangents"] = 2] = "Tangents";
    GeometryFlags[GeometryFlags["Uv0"] = 4] = "Uv0";
    GeometryFlags[GeometryFlags["Colors"] = 8] = "Colors";
    GeometryFlags[GeometryFlags["Skin0"] = 16] = "Skin0";
    GeometryFlags[GeometryFlags["Skin1"] = 32] = "Skin1";
})(GeometryFlags || (GeometryFlags = {}));
function expectedPayloadLength(opcode) {
    switch (opcode) {
        case 1 /* Command.Scene */:
            return 12;
        case 2 /* Command.Texture */:
            return 48;
        case 3 /* Command.Material */:
            return 96;
        case 4 /* Command.TransformNode */:
        case 5 /* Command.Skeleton */:
            return 20;
        case 6 /* Command.Geometry */:
            return 60;
        case 7 /* Command.Mesh */:
            return 40;
        case 8 /* Command.Instance */:
            return 16;
        case 9 /* Command.Animation */:
            return 32;
        case 10 /* Command.AnalyticPrimitive */:
            return 44;
        case 11 /* Command.ThinInstances */:
            return 12;
        case 12 /* Command.MorphTarget */:
            return 32;
        default:
            throw new Error(`Unknown OpenUSD Babylon command opcode ${opcode}.`);
    }
}
export function readCommands(buffer) {
    const view = new DataView(buffer);
    if (view.byteLength < 16 || view.getUint32(0, true) !== COMMAND_MAGIC) {
        throw new Error("Invalid OpenUSD Babylon command buffer.");
    }
    if (view.getUint16(4, true) !== PROTOCOL_VERSION) {
        throw new Error(`Unsupported OpenUSD Babylon protocol ${view.getUint16(4, true)}.`);
    }
    const count = view.getUint32(8, true);
    const commands = [];
    let offset = 16;
    for (let index = 0; index < count; ++index) {
        if (offset + 8 > view.byteLength) {
            throw new Error("Truncated OpenUSD Babylon command header.");
        }
        const opcode = view.getUint16(offset, true);
        const flags = view.getUint16(offset + 2, true);
        const payloadLength = view.getUint32(offset + 4, true);
        const payloadOffset = offset + 8;
        const expectedLength = expectedPayloadLength(opcode);
        if (payloadLength !== expectedLength) {
            throw new Error(`Invalid payload length ${payloadLength} for command ${opcode}; ` + `expected ${expectedLength}.`);
        }
        if (payloadOffset + payloadLength > view.byteLength) {
            throw new Error("Truncated OpenUSD Babylon command payload.");
        }
        commands.push({ opcode, flags, payloadOffset, payloadLength });
        offset = payloadOffset + payloadLength;
    }
    if (offset !== view.byteLength) {
        throw new Error("Unexpected trailing data in OpenUSD Babylon command buffer.");
    }
    return commands;
}
export class PayloadReader {
    constructor(buffer, offset, length) {
        _PayloadReader_instances.add(this);
        _PayloadReader_view.set(this, void 0);
        _PayloadReader_end.set(this, void 0);
        __classPrivateFieldSet(this, _PayloadReader_view, new DataView(buffer), "f");
        this.offset = offset;
        __classPrivateFieldSet(this, _PayloadReader_end, offset + length, "f");
    }
    u32() {
        __classPrivateFieldGet(this, _PayloadReader_instances, "m", _PayloadReader_require).call(this, 4);
        const value = __classPrivateFieldGet(this, _PayloadReader_view, "f").getUint32(this.offset, true);
        this.offset += 4;
        return value;
    }
    f32() {
        __classPrivateFieldGet(this, _PayloadReader_instances, "m", _PayloadReader_require).call(this, 4);
        const value = __classPrivateFieldGet(this, _PayloadReader_view, "f").getFloat32(this.offset, true);
        this.offset += 4;
        return value;
    }
}
_PayloadReader_view = new WeakMap(), _PayloadReader_end = new WeakMap(), _PayloadReader_instances = new WeakSet(), _PayloadReader_require = function _PayloadReader_require(byteLength) {
    if (this.offset + byteLength > __classPrivateFieldGet(this, _PayloadReader_end, "f")) {
        throw new Error("OpenUSD Babylon command payload read exceeded its bounds.");
    }
};
//# sourceMappingURL=usdCommandProtocol.js.map